/**
 * Job Queue Progress Store - SIMPLIFIED
 * Single event source: scraping:progress only
 * No merging, no confusion, just simple state updates
 */

import { useWebSocket } from "@/composables/useWebSocket";
import type { WSEventPayload } from "@shared/types";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

export interface JobQueueContext {
  jobId: string;
  currentRound: number;
  totalRounds: number;
  resultsCount: number;
  brand?: string;
  model?: string;
  fin?: string;
  jobsTotal?: number;
  jobIndex?: number;
  subState?: "started" | "navigating" | "page_loaded" | "extracting" | "completed" | "failed";
  timestamp: string;
}

export const useJobQueueProgressStore = defineStore(
  "jobQueueProgress",
  () => {
    const currentJob = ref<JobQueueContext | null>(null);

    const { on } = useWebSocket();

    // Listen ONLY to scraping:progress - single source of truth
    on(
      "scraping:progress",
      (data: WSEventPayload["scraping:progress"]) => {

          // Job started - create new entry
          if (data.type === "job_started") {
            currentJob.value = {
              jobId: data.jobId,
              currentRound: data.details?.currentRound ?? 1,
              totalRounds: data.details?.totalRounds ?? 1,
              resultsCount: 0,
              brand: data.details?.brand,
              model: data.details?.model,
              fin: data.details?.fin,
              jobsTotal: data.details?.jobsTotal,
              jobIndex: data.details?.jobIndex,
              subState: "started",
              timestamp: data.timestamp ?? new Date().toISOString(),
            };
            return;
          }

          // Update existing job only
          if (!currentJob.value || data.jobId !== currentJob.value.jobId) {
            return;
          }

          // Update round information (most important!)
          if (data.details?.currentRound !== undefined) {
            currentJob.value.currentRound = data.details.currentRound;
          }
          if (data.details?.totalRounds !== undefined) {
            currentJob.value.totalRounds = data.details.totalRounds;
          }

          // Update vehicle info if present
          if (data.details?.brand) {
            currentJob.value.brand = data.details.brand;
          }
          if (data.details?.model) {
            currentJob.value.model = data.details.model;
          }
          if (data.details?.fin) {
            currentJob.value.fin = data.details.fin;
          }

          // Update queue info
          if (data.details?.jobsTotal !== undefined) {
            currentJob.value.jobsTotal = data.details.jobsTotal;
          }
          if (data.details?.jobIndex !== undefined) {
            currentJob.value.jobIndex = data.details.jobIndex;
          }

          // Update results count
          if (data.details?.resultsCount !== undefined) {
            currentJob.value.resultsCount = data.details.resultsCount;
          }

          // Update sub-state based on event type
          switch (data.type) {
            case "job_started":
              currentJob.value.subState = "started";
              break;
            case "navigating":
              currentJob.value.subState = "navigating";
              break;
            case "page_loaded":
              currentJob.value.subState = "page_loaded";
              break;
            case "extracting":
              currentJob.value.subState = "extracting";
              break;
            case "job_complete":
              currentJob.value.subState = "completed";
              // Auto-clear after 8 seconds
              setTimeout(() => {
                if (currentJob.value?.jobId === data.jobId) {
                  currentJob.value = null;
                }
              }, 8000);
              break;
            case "job_failed":
              currentJob.value.subState = "failed";
              // Auto-clear after 8 seconds
              setTimeout(() => {
                if (currentJob.value?.jobId === data.jobId) {
                  currentJob.value = null;
                }
              }, 8000);
              break;
          }

          currentJob.value.timestamp = data.timestamp ?? new Date().toISOString();
      },
    );

    // Computed: Friendly sub-state label
    const subStateLabel = computed(() => {
      if (!currentJob.value?.subState) return "";

      switch (currentJob.value.subState) {
        case "started":
          return "Start";
        case "navigating":
          return "Navigate";
        case "page_loaded":
          return "Loaded";
        case "extracting":
          return "Extracting";
        case "completed":
          return "Complete";
        case "failed":
          return "Failed";
        default:
          return "";
      }
    });

    // Computed: Formatted queue display
    const queueDisplay = computed(() => {
      if (!currentJob.value) return null;

      const parts: string[] = [];

      // Job position: "Job 1/2"
      if (currentJob.value.jobsTotal !== undefined && currentJob.value.jobIndex !== undefined) {
        parts.push(`Job ${currentJob.value.jobIndex + 1}/${currentJob.value.jobsTotal}`);
      }

      // Round: "Round 2/3"
      parts.push(`Round ${currentJob.value.currentRound}/${currentJob.value.totalRounds}`);

      // Vehicle info: "VW ID.5" or "FIN: xxx"
      const vehicleInfo = currentJob.value.brand && currentJob.value.model
        ? `${currentJob.value.brand} ${currentJob.value.model}`
        : currentJob.value.fin
          ? `FIN: ${currentJob.value.fin}`
          : undefined;

      if (vehicleInfo) {
        parts.push(vehicleInfo);
      }

      // Sub-state
      if (subStateLabel.value) {
        parts.push(`→ ${subStateLabel.value}`);
      }

      // Results count
      if (currentJob.value.resultsCount > 0) {
        parts.push(`(${currentJob.value.resultsCount} found)`);
      }

      return parts.join(" ");
    });

    // Computed: Status icon
    const statusIcon = computed(() => {
      if (!currentJob.value) return null;

      switch (currentJob.value.subState) {
        case "started":
          return "🚀";
        case "navigating":
          return "🌐";
        case "page_loaded":
          return "✅";
        case "extracting":
          return "⚙️";
        case "completed":
          return "✅";
        case "failed":
          return "❌";
        default:
          return "🔵";
      }
    });

    const clear = () => {
      currentJob.value = null;
    };

    return {
      currentJob,
      queueDisplay,
      statusIcon,
      clear,
    };
  },
);
